import React from 'react';
import useMetadataStore from '@store/useMetadataStore';
import useGameStore from '@store/useGameStore';
import { getEffectIcon } from '@utils/statIcons';
import { getAssetPath } from '@utils/brandHelper';
import HoverClickPopup from '@components/ui/general/HoverClickPopup';
import UnitIcon from '@components/game/objects/UnitIcon';
import CooldownBlocks from '@components/ui/game/cooldown-blocks';
import './EffectIcon.css';

/**
 * EffectIcon Component
 * Displays effect icons with two variants:
 * - 'icon' (default): Just the icon for EffectBar with hover/click popup
 * - 'with-name': Icon + name for Stats popup
 * 
 * @param {number} effectId - The effect ID from database
 * @param {object} activeEffect - The active effect instance with runtime data
 * @param {string} variant - 'icon' or 'with-name'
 * @param {string} className - Additional CSS classes
 */
const EffectIcon = ({ effectId, activeEffect = null, variant = 'icon', className = '' }) => {
  // Get effect data from metadata store
  const effect = useMetadataStore.getState().getEffectById(effectId);
  
  if (!effect) {
    //console.warn('[EffectIcon] Effect not found for ID:', effectId);
    return null;
  }
  
  // Get icon from animation_data or use fallback
  const iconData = effect.animation_data?.icon || {};
  const iconDisplay = getEffectIconDisplay(iconData, effect.slug);
  
  // Icon only variant (for EffectBar) - with popup if activeEffect provided
  if (variant === 'icon') {
    // Validate category_color exists (should be validated in store, but double-check)
    if (!effect.category_color) {
      console.error(`[EffectIcon] Missing category_color for effect: ${effect.slug} (id: ${effect.id})`);
      return null; // Store should have handled this, but fail gracefully
    }
    const categoryColor = effect.category_color;

    // Check if this effect has duration (non-passive with counters)
    const hasDuration = activeEffect &&
      !activeEffect.effect?.is_passive &&
      activeEffect.variables?.on_turn_start_counters !== undefined &&
      activeEffect.variables?.max_on_turn_start_counters;

    const iconElement = (
      <div
        className={`effect-icon ${className}`}
        style={{ borderColor: categoryColor }}
      >
        {iconDisplay}
        {/* Duration indicator for non-passive effects - just show number */}
        {hasDuration && (
          <span className="effect-duration-badge">
            {activeEffect.variables.on_turn_start_counters}
          </span>
        )}
      </div>
    );

    // If no activeEffect, just show icon without popup
    if (!activeEffect) {
      return iconElement;
    }

    // Get source unit for the effect
    const sourceUnit = activeEffect.source?.type === 'unit' && activeEffect.source?.id
      ? useGameStore.getState().getUnitOrAvailableUnitById(activeEffect.source.id)
      : null;
    
    // Render with popup
    return (
      <HoverClickPopup trigger={iconElement}>
        {/* Header with icon and name */}
        <div className="click-popup-header">
          <div className="click-popup-title-group">
            <span className="click-popup-icon">{iconDisplay}</span>
            <span className="click-popup-title">{effect.name}</span>
          </div>
        </div>

        {/* Description */}
        <div className="click-popup-description">
          {effect.description || 'No description available'}
        </div>

        {/* Separator */}
        <div className="click-popup-separator" />

        {/* Details */}
        <div className="click-popup-details">
          {/* Duration */}
          <div className="click-popup-detail-row">
            <span className="click-popup-detail-label">Duration:</span>
            <span className="click-popup-detail-value">
              {activeEffect.effect.is_passive ? (
                // Passive effects: show "Permanent" or counter info
                activeEffect.counter_state ? (
                  `Permanent (${activeEffect.counter_state.current_turn_triggers || 0}/${activeEffect.counter_state.max_per_turn || 0} this turn)`
                ) : 'Permanent'
              ) : (
                // Temporary effects: show CooldownBlocks widget
                <CooldownBlocks
                  cooldownMax={activeEffect.variables?.max_on_turn_start_counters}
                  cooldownCurrent={activeEffect.variables?.on_turn_start_counters}
                  mode="duration"
                />
              )}
            </span>
          </div>

          {/* Counter Usage - only show for counter effects */}
          {activeEffect.counter_state && (
            <div className="click-popup-detail-row">
              <span className="click-popup-detail-label">Counter Usage:</span>
              <span className="click-popup-detail-value">
                {(() => {
                  const current = activeEffect.counter_state.current_turn_triggers || 0;
                  const max = activeEffect.counter_state.max_per_turn || 0;
                  const remaining = Math.max(0, max - current);

                  if (current === 0) {
                    return <span className="counter-ready">Ready ({max} available)</span>;
                  } else if (remaining > 0) {
                    return <span className="counter-partial">{remaining} remaining (used {current})</span>;
                  } else {
                    return <span className="counter-depleted">Depleted ({current}/{max})</span>;
                  }
                })()}
              </span>
            </div>
          )}

          {/* Source */}
          <div className="click-popup-detail-row">
            <span className="click-popup-detail-label">Source:</span>
            <span className="click-popup-detail-value source">
              {sourceUnit ? (
                <>
                  <UnitIcon unitId={sourceUnit.id} size="tiny" />
                  <span>{sourceUnit.name}</span>
                </>
              ) : (
                activeEffect.source?.name || 'Unknown'
              )}
            </span>
          </div>
        </div>
      </HoverClickPopup>
    );
  }
  
  // Icon with name variant (for Stats popup)
  if (variant === 'with-name') {
    return (
      <div className={`effect-icon-with-name ${className}`}>
        <span className="effect-icon-display">
          {iconDisplay}
        </span>
        <span className="effect-name">
          {effect.name}
        </span>
      </div>
    );
  }
  
  console.warn('[EffectIcon] Unknown variant:', variant);
  return null;
};

/**
 * Helper function to get the icon display
 * Uses animation_data from backend or falls back to statIcons
 */
const getEffectIconDisplay = (iconData, slug) => {
  // Check if we have icon data from backend
  if (iconData?.value) {
    switch (iconData.type) {
      case 'unicode':
        return <span className="unicode-icon">{iconData.value}</span>;

      case 'image':
        // Backend sends relative paths like 'images/effects/fire.svg'
        // We need to prepend brand directory
        return <img src={getAssetPath(iconData.value)} alt={iconData.fallback || slug} />;

      case 'css-class':
        return <i className={iconData.value}></i>;

      default:
        console.warn('[EffectIcon] Unknown icon type:', iconData.type);
    }
  }

  // Fallback to statIcons utility
  const fallbackIcon = getEffectIcon(slug);
  if (fallbackIcon) {
    return fallbackIcon;
  }

  // Last resort fallback
  return <span className="fallback-icon">?</span>;
};

export default EffectIcon;